import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import api from "@/lib/api";
import { Building2, ClipboardList, HardHat, Users, MapPin, ArrowRight, Wrench, PaintRoller, Zap, Trees, Sparkles, ShieldCheck, FileText, Camera, Receipt } from "lucide-react";
import { PublicPlanCard } from "@/components/PublicPlanCard";
import { PUBLIC_PLANS, MOST_POPULAR } from "@/lib/publicPlans";

const HERO_IMG = "https://images.pexels.com/photos/18153132/pexels-photo-18153132.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

const SERVICES = [
  { icon: Wrench, name: "Handyman & Repairs" },
  { icon: Zap, name: "Plumbing & Electrical" },
  { icon: PaintRoller, name: "Painting & Drywall" },
  { icon: Trees, name: "Landscaping" },
  { icon: Sparkles, name: "Cleaning & Turnovers" },
  { icon: ShieldCheck, name: "Property Preservation" },
];

const STEPS = [
  { icon: ClipboardList, title: "Submit a Request", desc: "Property managers submit maintenance requests in seconds — with photos, urgency, and access details." },
  { icon: Users, title: "We Dispatch a Pro", desc: "SJ reviews, assigns a vetted contractor, and coordinates scheduling and estimates." },
  { icon: Camera, title: "Track & Verify", desc: "Follow job status live with before/after photos and real-time updates." },
  { icon: Receipt, title: "Approve & Pay", desc: "Approve estimates and pay invoices securely — all in one place." },
];

const SERVE = [
  "Property Managers", "Landlords", "Real Estate Investors", "Apartment Communities",
  "HOAs", "Short-Term Rentals", "Commercial Owners",
];

export default function Home() {
  const navigate = useNavigate();
  const [markets, setMarkets] = useState({ live: [], coming_soon: [] });
  useEffect(() => { api.get("/public/markets").then(({ data }) => setMarkets(data)).catch(() => {}); }, []);
  return (
    <div className="bg-white">
      {/* Nav */}
      <header className="fixed top-0 inset-x-0 z-50 backdrop-blur-xl bg-slate-950/70 border-b border-white/10">
        <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 text-white">
            <div className="w-9 h-9 rounded-lg bg-blue-600 flex items-center justify-center"><Building2 className="w-5 h-5" /></div>
            <span className="font-head font-bold text-lg">SJ Property Care</span>
          </Link>
          <div className="flex items-center gap-2">
            <Button data-testid="nav-pm-login" variant="ghost" className="text-white hover:bg-white/10 hidden sm:inline-flex" onClick={() => navigate("/login")}>Property Manager</Button>
            <Button data-testid="nav-plans" variant="ghost" className="text-white hover:bg-white/10 hidden sm:inline-flex" onClick={() => navigate("/plans")}>Plans</Button>
            <Button data-testid="nav-contractor-login" variant="ghost" className="text-white hover:bg-white/10 hidden sm:inline-flex" onClick={() => navigate("/login")}>Contractor</Button>
            <Button data-testid="nav-get-started" className="bg-blue-600 hover:bg-blue-700 text-white rounded-lg" onClick={() => navigate("/request-service")}>Request Service</Button>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="relative min-h-[92vh] flex items-center bg-slate-950 overflow-hidden">
        <div className="absolute inset-0">
          <img src={HERO_IMG} alt="Premium apartment building" className="w-full h-full object-cover opacity-40" />
          <div className="absolute inset-0 bg-gradient-to-r from-slate-950 via-slate-950/80 to-slate-950/40" />
        </div>
        <div className="relative max-w-7xl mx-auto px-6 pt-24 grid lg:grid-cols-12 gap-8 items-center">
          <div className="lg:col-span-7 animate-fade-up">
            <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-blue-600/20 border border-blue-500/30 text-blue-300 text-xs font-semibold uppercase tracking-wider">
              <span className="w-1.5 h-1.5 rounded-full bg-blue-400" /> SJ Contract Solutions LLC
            </span>
            <h1 className="mt-6 font-head text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight text-white leading-[1.05]">
              Property Maintenance.<br /><span className="text-blue-400">One Request. Every Trade.</span>
            </h1>
            <p className="mt-6 text-base md:text-lg text-slate-300 max-w-xl leading-relaxed">
              One place for property managers and investors to handle repairs, maintenance, turnovers, contractors, estimates, photos, and invoices.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Button data-testid="hero-request-service" size="lg" className="bg-blue-600 hover:bg-blue-700 text-white rounded-lg h-12 px-7" onClick={() => navigate("/request-service")}>
                Request Service <ArrowRight className="w-4 h-4 ml-1" />
              </Button>
              <Button data-testid="hero-pm-login" size="lg" variant="outline" className="rounded-lg h-12 px-7 bg-white/5 border-white/20 text-white hover:bg-white/10" onClick={() => navigate("/login")}>
                Property Manager Login
              </Button>
              <Button data-testid="hero-contractor-login" size="lg" variant="outline" className="rounded-lg h-12 px-7 bg-white/5 border-white/20 text-white hover:bg-white/10" onClick={() => navigate("/register/contractor")}>
                <HardHat className="w-4 h-4 mr-1" /> Contractor Login
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing Preview */}
      <section className="py-20 md:py-24 bg-slate-50" data-testid="pricing-preview">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center max-w-2xl mx-auto">
            <span className="text-xs uppercase tracking-[0.15em] font-bold text-blue-600">Plans &amp; Pricing</span>
            <h2 className="mt-3 font-head text-3xl md:text-4xl font-medium text-slate-900">Property Care Plans Built for Every Portfolio</h2>
          </div>
          <div className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {PUBLIC_PLANS.map((p) => (
              <PublicPlanCard key={p.lookup_key} plan={p} popular={p.lookup_key === MOST_POPULAR}
                onGetStarted={(plan) => {
                  if (plan.amount === null) { navigate("/request-service"); return; }
                  localStorage.setItem("sj_pending_plan", plan.lookup_key);
                  navigate(`/register/client?plan=${plan.lookup_key}`);
                }} />
            ))}
          </div>
          <div className="mt-10 text-center">
            <Button data-testid="compare-all-plans" size="lg" variant="outline" className="rounded-lg h-12 px-8 border-slate-300 text-slate-800 hover:bg-white" onClick={() => navigate("/plans")}>
              Compare All Plans <ArrowRight className="w-4 h-4 ml-1" />
            </Button>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-20 md:py-28 max-w-7xl mx-auto px-6">
        <div className="max-w-2xl">
          <span className="text-xs uppercase tracking-[0.15em] font-bold text-blue-600">How It Works</span>
          <h2 className="mt-3 font-head text-3xl md:text-4xl font-medium text-slate-900">Your maintenance department, streamlined.</h2>
        </div>
        <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {STEPS.map((s, i) => (
            <div key={i} className="bg-slate-50 border border-slate-200 rounded-xl p-6 hover:border-blue-300 hover:-translate-y-1 transition-[transform,border-color] duration-300">
              <div className="w-11 h-11 rounded-lg bg-blue-600 text-white flex items-center justify-center"><s.icon className="w-5 h-5" /></div>
              <div className="mt-4 text-xs font-bold text-slate-400">STEP {i + 1}</div>
              <h3 className="mt-1 font-head text-lg font-semibold text-slate-900">{s.title}</h3>
              <p className="mt-2 text-sm text-slate-600 leading-relaxed">{s.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Services */}
      <section className="py-20 bg-slate-950">
        <div className="max-w-7xl mx-auto px-6">
          <span className="text-xs uppercase tracking-[0.15em] font-bold text-blue-400">Services</span>
          <h2 className="mt-3 font-head text-3xl md:text-4xl font-medium text-white">Every trade, one vendor relationship.</h2>
          <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {SERVICES.map((s, i) => (
              <div key={i} className="flex items-center gap-4 bg-slate-900 border border-slate-800 rounded-xl p-5 hover:border-blue-600 transition-colors">
                <div className="w-11 h-11 rounded-lg bg-blue-600/20 text-blue-400 flex items-center justify-center"><s.icon className="w-5 h-5" /></div>
                <span className="font-medium text-white">{s.name}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Who We Serve */}
      <section className="py-20 md:py-28 max-w-7xl mx-auto px-6 grid lg:grid-cols-2 gap-12 items-center">
        <div>
          <span className="text-xs uppercase tracking-[0.15em] font-bold text-blue-600">Who We Serve</span>
          <h2 className="mt-3 font-head text-3xl md:text-4xl font-medium text-slate-900">Built for property professionals.</h2>
          <p className="mt-4 text-slate-600 leading-relaxed">From single-family rentals to apartment communities and commercial portfolios — SJ Property Care scales with you.</p>
          <div className="mt-8 flex flex-wrap gap-2.5">
            {SERVE.map((s) => (
              <span key={s} className="px-4 py-2 rounded-full bg-blue-50 border border-blue-100 text-blue-800 text-sm font-medium">{s}</span>
            ))}
          </div>
        </div>
        <div className="rounded-2xl overflow-hidden shadow-xl">
          <img src="https://images.unsplash.com/photo-1543269664-76bc3997d9ea?crop=entropy&cs=srgb&fm=jpg&q=85&w=940" alt="Property manager using platform" className="w-full h-full object-cover" />
        </div>
      </section>

      {/* Platform */}
      <section className="py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-6">
          <span className="text-xs uppercase tracking-[0.15em] font-bold text-blue-600">Property Manager Platform</span>
          <h2 className="mt-3 font-head text-3xl md:text-4xl font-medium text-slate-900 max-w-2xl">Everything you need to run maintenance in one dashboard.</h2>
          <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { icon: Building2, t: "Property Portfolio", d: "Manage all properties, units, tenants, and access details." },
              { icon: ClipboardList, t: "Work Orders", d: "Submit and track requests through every stage." },
              { icon: FileText, t: "Digital Estimates", d: "Review and approve estimates in one click." },
              { icon: Receipt, t: "Invoices & Payments", d: "View invoices and pay securely online." },
            ].map((f, i) => (
              <div key={i} className="bg-white border border-slate-200 rounded-xl p-6 shadow-sm">
                <f.icon className="w-6 h-6 text-blue-600" />
                <h3 className="mt-4 font-head font-semibold text-slate-900">{f.t}</h3>
                <p className="mt-2 text-sm text-slate-600">{f.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Service Areas */}
      <section className="py-20 md:py-28 max-w-7xl mx-auto px-6 text-center">
        <MapPin className="w-8 h-8 text-blue-600 mx-auto" />
        <h2 className="mt-4 font-head text-3xl md:text-4xl font-medium text-slate-900">Service Areas</h2>
        {markets.live.length > 0 ? (
          <>
            <p className="mt-4 text-slate-600 max-w-xl mx-auto">Actively serving {markets.live.map((m) => m.name).join(" and ")} — with a growing contractor network.</p>
            <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-3 gap-5 text-left">
              {markets.live.map((m) => (
                <div key={m.id} data-testid={`service-area-${m.code}`} className="bg-white border border-slate-200 rounded-xl p-6 hover:border-blue-300 transition-colors">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2"><div className="w-9 h-9 rounded-lg bg-blue-50 flex items-center justify-center font-head font-bold text-blue-600">{m.code}</div><h3 className="font-head font-semibold text-slate-900">{m.name}</h3></div>
                    <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700">Live</span>
                  </div>
                  <div className="mt-3 flex flex-wrap gap-1.5">
                    {(m.cities || []).map((c) => <span key={c} className="text-xs px-2 py-0.5 rounded-full bg-slate-100 text-slate-600 flex items-center gap-1"><MapPin className="w-3 h-3" />{c}</span>)}
                  </div>
                </div>
              ))}
            </div>
          </>
        ) : (
          <p className="mt-4 text-slate-600 max-w-xl mx-auto">Serving a growing network of markets across the Southeast.</p>
        )}
        {markets.coming_soon.length > 0 && (
          <div className="mt-12">
            <span className="text-xs uppercase tracking-[0.15em] font-bold text-amber-600">Coming Soon</span>
            <div className="mt-4 flex flex-wrap justify-center gap-2.5">
              {markets.coming_soon.map((m) => (
                <span key={m.id} data-testid={`coming-soon-${m.code}`} className="px-4 py-2 rounded-full bg-amber-50 border border-amber-100 text-amber-800 text-sm font-medium">
                  {m.name}{m.cities?.length ? ` — ${m.cities.join(", ")}` : ""}
                </span>
              ))}
            </div>
          </div>
        )}
      </section>

      {/* Final CTA */}
      <section className="py-24 bg-blue-600">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="font-head text-3xl md:text-5xl font-semibold text-white leading-tight">Let SJ Become Your Maintenance Department.</h2>
          <div className="mt-8 flex flex-wrap justify-center gap-3">
            <Button data-testid="cta-request-service" size="lg" className="bg-white text-blue-700 hover:bg-slate-100 rounded-lg h-12 px-8" onClick={() => navigate("/request-service")}>Request Service</Button>
            <Button data-testid="cta-contractor" size="lg" variant="outline" className="rounded-lg h-12 px-8 bg-transparent border-white text-white hover:bg-white/10" onClick={() => navigate("/register/contractor")}>Apply as Contractor</Button>
          </div>
        </div>
      </section>

      <footer className="bg-slate-950 text-slate-400 py-10">
        <div className="max-w-7xl mx-auto px-6 flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2 text-white">
            <div className="w-8 h-8 rounded-lg bg-blue-600 flex items-center justify-center"><Building2 className="w-4 h-4" /></div>
            <span className="font-head font-bold">SJ Property Care</span>
          </div>
          <p className="text-sm">© {new Date().getFullYear()} SJ Contract Solutions LLC. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
}
